import { clearSessionStorage } from '@/hooks/useSessionStorage';
import { LOCAL_FILTERS_STORAGE_MAP } from './localFiltersKeys';
import { createContext, useContext, useCallback, useRef, useMemo, useState, FC, ReactNode } from 'react';

export interface LocalFiltersEntry<T = Record<string, unknown>> {
  getFilters: () => T;
  setFilters: (filters: T) => void;
  getDefaultFilters?: () => T;
}

type LocalFiltersMap = Record<string, Record<string, unknown>>;

// The registry stores heterogeneous filter types (InsightFilters,
// ChannelFiltersState, etc.) — the generic on register() preserves
// type safety at each call site while the internal map erases to unknown.
interface LocalFiltersRegistryContextType {
  register: <T extends Record<string, unknown>>(key: string, entry: LocalFiltersEntry<T>) => void;
  unregister: (key: string) => void;
  getAllLocalFilters: () => LocalFiltersMap;
  getDefaultLocalFilters: () => LocalFiltersMap;
  clearAllLocalFilters: () => void;
  version: number;
}

const LocalFiltersRegistryContext = createContext<LocalFiltersRegistryContextType>({
  register: () => {},
  unregister: () => {},
  getAllLocalFilters: () => ({}),
  getDefaultLocalFilters: () => ({}),
  clearAllLocalFilters: () => {},
  version: 0,
});

interface LocalFiltersRegistryProviderProps {
  children: ReactNode;
}

export const LocalFiltersRegistryProvider: FC<LocalFiltersRegistryProviderProps> = ({ children }) => {
  const registryRef = useRef<Record<string, LocalFiltersEntry<Record<string, unknown>>>>({});
  const [registryVersion, setRegistryVersion] = useState(0);

  const register = useCallback(<T extends Record<string, unknown>>(key: string, entry: LocalFiltersEntry<T>) => {
    registryRef.current[key] = entry as LocalFiltersEntry<Record<string, unknown>>;
    setRegistryVersion(currentVersion => currentVersion + 1);
  }, []);

  const unregister = useCallback((key: string) => {
    delete registryRef.current[key];
    setRegistryVersion(currentVersion => currentVersion + 1);
  }, []);

  const getAllLocalFilters = useCallback((): LocalFiltersMap => {
    const result: LocalFiltersMap = {};
    Object.entries(registryRef.current).forEach(([key, entry]) => {
      result[key] = entry.getFilters();
    });
    return result;
  }, []);

  const getDefaultLocalFilters = useCallback((): LocalFiltersMap => {
    const result: LocalFiltersMap = {};
    Object.entries(registryRef.current).forEach(([key, entry]) => {
      if (entry.getDefaultFilters) {
        result[key] = entry.getDefaultFilters();
      }
    });
    return result;
  }, []);

  const clearAllLocalFilters = useCallback(() => {
    const registeredKeys = new Set(Object.keys(registryRef.current));
    Object.values(registryRef.current).forEach(registryEntry => {
      registryEntry.setFilters({});
    });
    Object.entries(LOCAL_FILTERS_STORAGE_MAP).forEach(([sectionKey, storageKey]) => {
      if (!registeredKeys.has(sectionKey)) {
        clearSessionStorage(storageKey);
      }
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      register,
      unregister,
      getAllLocalFilters,
      version: registryVersion,
      getDefaultLocalFilters,
      clearAllLocalFilters,
    }),
    [register, unregister, getAllLocalFilters, registryVersion, getDefaultLocalFilters, clearAllLocalFilters],
  );

  return <LocalFiltersRegistryContext.Provider value={contextValue}>{children}</LocalFiltersRegistryContext.Provider>;
};

export const useLocalFiltersRegistry = () => useContext(LocalFiltersRegistryContext);

/**
 * Memoized snapshot of every registered local filter that only recomputes when
 * the registry changes.
 *
 * Registry entries live in a ref (so a page mutating its filters doesn't
 * re-render every consumer on each keystroke), which means `version` is the
 * reactive trigger that gates the ref read. This selector is the single place
 * that owns the ref/version coupling, so feature consumers get a stable,
 * inferable dependency and never need their own exhaustive-deps override.
 */
export const useAllLocalFilters = (): LocalFiltersMap => {
  const { getAllLocalFilters, version } = useLocalFiltersRegistry();

  return useMemo(
    () => getAllLocalFilters(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getAllLocalFilters, version],
  );
};
